import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DualPaymentForm from '../components/payment/DualPaymentForm';
import { useAuth } from '../contexts/AuthContext';

const MembershipPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  console.log('MembershipPayment component rendering...');

  // Get data passed from registration
  const { tier, discount, userData } = location.state || {};

  // Debug logging
  console.log('MembershipPayment - location.state:', location.state);
  console.log('MembershipPayment - tier:', tier);
  console.log('MembershipPayment - discount:', discount);
  console.log('MembershipPayment - userData:', userData);
  console.log('MembershipPayment - currentUser:', currentUser);

  useEffect(() => {
    // Only redirect if we're sure there's no data after a delay
    const timer = setTimeout(() => {
      if (!tier || !userData) {
        console.error('No tier or user data found after delay, redirecting to registration');
        navigate('/register');
        return;
      }

      // Redirect if user is not authenticated
      if (!currentUser) {
        console.error('User not authenticated, redirecting to login');
        navigate('/login');
        return;
      }
    }, 1000); // Wait 1 second for data to load

    return () => clearTimeout(timer);
  }, [tier, userData, currentUser, navigate]);

  const handlePaymentSuccess = (membershipData) => {
    console.log('Payment successful:', membershipData);
    // Add a small delay to ensure payment processing is complete
    setTimeout(() => {
      navigate('/membership/success', { replace: true });
    }, 1000);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    // Stay on payment page to allow retry
  };

  const handleCancel = () => {
    navigate('/register');
  };

  // Show loading if data is not available
  if (!tier || !userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apn-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  // Calculate final price
  const originalPrice = tier.price;
  const finalPrice = discount ? discount.discountedPrice : originalPrice;
  const savings = originalPrice - finalPrice;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Debug Info */}
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <h3 className="font-bold">DEBUG INFO:</h3>
          <p>Component is rendering!</p>
          <p>Tier: {tier ? tier.name : 'NO TIER'}</p>
          <p>User: {currentUser ? currentUser.email : 'NO USER'}</p>
          <p>Location state: {location.state ? 'EXISTS' : 'MISSING'}</p>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Complete Your Membership
          </h1>
          <p className="text-lg text-gray-600">
            You're just one step away from joining the APN community!
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Membership Tier:</span>
              <span className="font-semibold">{tier.name}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Original Price:</span>
              <span className={discount ? "line-through text-gray-400" : "font-semibold"}>
                ${originalPrice}
              </span>
            </div>
            
            {discount && (
              <>
                <div className="flex justify-between text-green-600">
                  <span>Discount ({discount.code}):</span>
                  <span>-${savings}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-green-600">
                  <span>Final Price:</span>
                  <span>${finalPrice}</span>
                </div>
              </>
            )}
            
            {!discount && (
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span>${finalPrice}</span>
              </div>
            )}
          </div>

          {/* Member Info */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Member Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Name:</span>
                <span className="ml-2 font-medium">{userData.firstName} {userData.lastName}</span>
              </div>
              <div>
                <span className="text-gray-600">Email:</span>
                <span className="ml-2 font-medium">{userData.email}</span>
              </div>
              <div>
                <span className="text-gray-600">Company:</span>
                <span className="ml-2 font-medium">{userData.company || 'Not specified'}</span>
              </div>
              <div>
                <span className="text-gray-600">Job Title:</span>
                <span className="ml-2 font-medium">{userData.jobTitle || 'Not specified'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Information</h2>
          
          <DualPaymentForm
            selectedTier={tier.key}
            discountCode={discount?.code || ''}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
          />
        </div>

        {/* Cancel Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleCancel}
            className="text-gray-600 hover:text-gray-800 underline"
          >
            ← Back to Registration
          </button>
        </div>
      </div>
    </div>
  );
};

export default MembershipPayment;
